import {ApDynformsConfigFieldset}          from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigComboBox}          from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynGridPropertyColumnConfig}     from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynformsConfigNumerictextbox}    from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ApDynformsValidator}               from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                        from '@angular/forms';
import {Observable}                        from 'rxjs';
import {ApDynGridGroupColumnConfig}        from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {ApDynGridColumnConfigBase}         from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {GetRoundNumericPipe}               from '../../../ap-utils';
import {Injectable}                        from '@angular/core';
import {ApTranslationService}              from '../../../ap-utils/service/ap-translation.service';
import {NFertilizationCommonEntryService}  from '../services/n-fertilization-common-entry.service';
import {NAppSettingsConfigBase}            from './base/n-app-settings-config.base';
import {ApDynformsConfigBase}              from '../../../ap-dynforms/config/ap-dynforms-config-base';
import {ApplicationSettingsRateEnums}      from '../../../stores/agronomic/application-settings.store';
import {OperationModesEnum}                from '../../../stores/common/operation-modes.store';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

@Injectable()
export class RapeFertilizationConfig extends NAppSettingsConfigBase {
  private _minAllowed = 0;
  private _maxAllowed = 250;

  constructor(private translationService: ApTranslationService,
              private nFertilizationCommonEntryService: NFertilizationCommonEntryService,
              private roundNumericPipe: GetRoundNumericPipe) {
    super();
  }

  public formBuilderConfig(values: any, formValues$: Observable<any>, applicationSettings: IApplicationSettings,
                           cropKey: number | undefined, avgNMin: number | undefined, isModalField: boolean,
                           applyBlinkEffectOnInit: boolean): ApDynformsConfigFieldset[] {
    const applyBlinkEffect = !(isModalField || !applyBlinkEffectOnInit);
    const parameters = applicationSettings.Parameter;
    const min$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Minimum']) && x['Minimum'] >= 0),
      map((v) => v['Minimum'])
    );
    const max$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Maximum']) && x['Maximum'] >= 0),
      map((v) => v['Maximum'])
    );
    const agronomicConfigs: ApDynformsConfigBase<any>[] = [
      new ApDynformsConfigNumerictextbox({
        key: 'Threshold',
        label: 'Global__Threshold_SN',
        value: values['Threshold'],
        value$: formValues$.pipe(
          filter(() => !isModalField),
          filter(formValues => Number.isFinite(formValues['ECStage'])),
          distinctUntilChanged((prev, curr) => prev['ECStage'] === curr['ECStage']),
          map(formValues => this.nFertilizationCommonEntryService.getThreshold(formValues['ECStage'], cropKey, avgNMin))
        ),
        min: 0,
        max: 100,
        step: 1,
        decimals: 0,
        format: 'n0',
        blinkOnInit: applyBlinkEffect,
        validators: [
          new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
        ]
      })
    ];
    switch (applicationSettings.TranslationKey) {
      case ApplicationSettingsRateEnums.FirstRate:
        agronomicConfigs.push(
          new ApDynformsConfigComboBox({
            key: 'DeadBioMass',
            label: 'Global__ExtinctBiomass',
            value: values['DeadBioMass'],
            options: [
              {value: 0, text: '0 %'},
              {value: 10, text: '10 %'},
              {value: 20, text: '20 %'},
              {value: 30, text: '30 %'},
              {value: 40, text: '40 %'},
            ],
            textField: 'text',
            valueField: 'value',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          })
        );
        break;
      case ApplicationSettingsRateEnums.SecondRate:
        agronomicConfigs.push(
          new ApDynformsConfigComboBox({
            key: 'YieldExpectation',
            label: 'Docu_Ini__YieldExpectation',
            value: values['YieldExpectation'],
            options: formValues$.pipe(
              map(() => this._getYieldExpectationOptions(applicationSettings.TranslationKey as ApplicationSettingsRateEnums))
            ),
            infoText: 'Global_Yield_Expectation_Text_Info',
            textField: 'text',
            valueField: 'value',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigComboBox({
            key: 'Potential',
            label: 'Global__N_MineralPot',
            value: values['Potential'] ?? 1,
            textField: 'text',
            valueField: 'value',
            translate: true,
            options: [
              {value: 0, text: 'AgriConnect_MineralPot_Low'},
              {value: 1, text: 'AgriConnect_MineralPot_Medium'},
              {value: 2, text: 'AgriConnect_MineralPot_High'}
            ],
            blinkOnInit: applyBlinkEffect,
          })
        );
        break;
    }

    agronomicConfigs.push(this.generateSlopeControls(parameters, values, !(isModalField || !applyBlinkEffect)));

    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        useMaxWidth: isModalField,
        config: agronomicConfigs
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            infoText: 'RapeFertilizationMinimumAdjustmentInfoText',
            value: values['Minimum'],
            value$: min$,
            min: this._minAllowed,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            infoText: 'RapeFertilizationMaximumAdjustmentInfoText',
            value: values['Maximum'],
            value$: max$,
            min: min$,
            max: this._maxAllowed,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['Constant'],
            min: min$,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          })
        ],
      })
    ];
  }

  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Control_Function',
        field: 'ControlFactor',
        hide: true,
        width: 50
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__KgNPerHa',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__Reference',
            field: 'Reference',
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Threshold',
            field: 'Threshold',
            hide: true,
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__RateConst',
            field: 'Constant',
            hide: true,
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'Minimum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'Maximum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }

  public updateFormValuesByParameters(values: any, cropKey: number | undefined, avgNMin: number | undefined,
                                      applicationSettings: IApplicationSettings | undefined): void {
    const parameters = applicationSettings.Parameter;
    if (!Number.isFinite(values['Threshold'])) {
      values['Threshold'] = this.nFertilizationCommonEntryService.getThreshold(values['ECStage'], cropKey, avgNMin);
    }
    if (!Number.isFinite(values['DeadBioMass']) && Number.isFinite(parameters?.deadBioMass)) {
      values['DeadBioMass'] = parameters.deadBioMass;
    } else {
      values['DeadBioMass'] = 0;
    }
    if (!Number.isFinite(values['Amount'])) {
      values['Amount'] = parameters?.targetValue ?? parameters.recommendation;
    }
    if (!Number.isFinite(values['Constant'])) {
      values['Constant'] = parameters?.uniform;
    }
    if (!Number.isFinite(values['Minimum'])) {
      values['Minimum'] = parameters?.min ?? this._minAllowed;
    }
    if (!Number.isFinite(values['Maximum'])) {
      values['Maximum'] = parameters?.max ?? this._maxAllowed;
    }
    if (!Number.isFinite(values['ControlFactor'])) {
      values['ControlFactor'] = parameters?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
    if (applicationSettings.TranslationKey === ApplicationSettingsRateEnums.FirstRate) {
      values['YieldExpectation'] = this.nFertilizationCommonEntryService.getYieldExpectation(
        OperationModesEnum.RapeFertilization
      );
      values['Potential'] = undefined;
    }
    if (applicationSettings.TranslationKey === ApplicationSettingsRateEnums.SecondRate) {
      values['DeadBioMass'] = undefined;
    }
  }

  private _getYieldExpectationOptions(applicationRate: ApplicationSettingsRateEnums): {
    value: number,
    text: string
  }[] {
    const value3Text = this.translationService.translate('Osr_YieldExpectation_Value3');
    const value4Text = this.translationService.translate('Osr_YieldExpectation_Value4');
    const value6Text = this.translationService.translate('Osr_YieldExpectation_Value6');
    if (applicationRate === ApplicationSettingsRateEnums.SecondRate) {
      return [
        {value: 3, text: `${value3Text} (${this.translationService.translate('Osr_YieldExpectation_Value3_2ndRate')})`},
        {value: 4, text: `${value4Text} (${this.translationService.translate('Osr_YieldExpectation_Value4_2ndRate')})`},
        {value: 6, text: `${value6Text} (${this.translationService.translate('Osr_YieldExpectation_Value6_2ndRate')})`}
      ];
    }
    return [
      {value: 3, text: value3Text},
      {value: 4, text: value4Text},
      {value: 6, text: value6Text}
    ];
  }
}
